import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import { Grid, TextField, MenuItem } from "@mui/material";

export default function ChildStepRejectForm({ stepIndex, childStepIndex }) {
    const { values, setFieldValue, touched, errors } = useFormikContext();

    // Paths for nested childSteps
    const base = `steps[${stepIndex}].childSteps[${childStepIndex}]`;
    const actionPath = `${base}.onRejectAction`;
    const gotoPath = `${base}.onRejectGoToSequence`;
    const msgPath = `${base}.onRejectMessage`;

    const childSteps = values?.steps?.[stepIndex]?.childSteps || [];
    const currentChildStep = childSteps?.[childStepIndex];

    const rejectOptions = [
        { value: "", label: "No special action" },
        { value: "gotostep", label: "Go to step" },
    ];

    // Options for the dropdown (Lists all child steps in this parent step)
    const childStepOptions = useMemo(() => {
        return childSteps.map((s, idx) => ({
            value: s.sequence || idx + 1,
            label: `${s.sequence || idx + 1}. ${s.childStepName?.trim() ? s.childStepName : "Untitled Child Step"}`,
        }));
    }, [childSteps]);

    const currentAction = currentChildStep?.onRejectAction ?? "";
    const currentGoto = currentChildStep?.onRejectGoToSequence ?? "";

    // Error handling for nested fields
    const gotoErr =
        touched?.steps?.[stepIndex]?.childSteps?.[childStepIndex]?.onRejectGoToSequence &&
        errors?.steps?.[stepIndex]?.childSteps?.[childStepIndex]?.onRejectGoToSequence;

    return (
        <Grid container spacing={2}>
            {/* Rejection Action Select */}
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                    select
                    fullWidth
                    label="On Reject Action (Child)"
                    name={actionPath}
                    value={currentAction}
                    onChange={(e) => {
                        const v = e.target.value || "";
                        setFieldValue(actionPath, v);

                        // Reset the target sequence if action isn't 'gotostep'
                        if (v !== "gotostep") {
                            setFieldValue(gotoPath, 0);
                        }
                    }}
                >
                    {rejectOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            {/* Go To Sequence Select */}
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                    select
                    fullWidth
                    disabled={currentAction !== "gotostep"}
                    label="Go to Child Sequence"
                    name={gotoPath}
                    value={currentAction === "gotostep" ? currentGoto : ""}
                    onChange={(e) => {
                        const v = e.target.value === "" ? 0 : Number(e.target.value);
                        setFieldValue(gotoPath, v);
                    }}
                    error={Boolean(gotoErr)}
                    helperText={gotoErr ? String(gotoErr) : ""}
                >
                    <MenuItem value="">Select child step</MenuItem>
                    {childStepOptions
                        // Filter out the current child step to prevent infinite loops
                        .filter((opt) => opt.value !== (currentChildStep?.sequence || childStepIndex + 1))
                        .map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                </TextField>
            </Grid>

            {/* Rejection Message */}
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                    fullWidth
                    label="Rejection Message"
                    name={msgPath}
                    value={currentChildStep?.onRejectMessage || ""}
                    onChange={(e) => setFieldValue(msgPath, e.target.value)}
                    placeholder="Enter reason for rejection..."
                />
            </Grid>
        </Grid>
    );
}